import { Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatSidenav } from '@angular/material/sidenav';
import { RedirectService } from '../../../../services/redirect.service';
import { SelectionModel } from '@angular/cdk/collections';
import { InvoicePdfService } from '../../invoices/service/invoice-pdf.service';
import { PreviousPaymentsService } from '../../../../services/payments/previous-payments.service';
import { PaymentMethodType, PaymentsApiService, PaymentUi } from '@aiwyn/api';
import { ConfigService } from '@aiwyn/config';
import { ROUTE_TOKEN_ENGAGEMENTS_BASE, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';
import { AbstractPaymentMethod } from '@aiwyn/common/payments';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { PaymentDateService } from '../services/payment-date.service';

@Component({
  selector: 'app-paid',
  templateUrl: './paid.component.html',
  styleUrls: ['./paid.component.scss'],
  standalone: false,
})
export class PaidComponent implements OnInit, OnChanges, OnDestroy {
  private readonly _unsubscribe$ = new Subject<void>();

  ROUTE_TOKEN_MAIN_BASE = ROUTE_TOKEN_MAIN_BASE;
  ROUTE_TOKEN_ENGAGEMENTS_BASE = ROUTE_TOKEN_ENGAGEMENTS_BASE;
  Math = Math;

  // The <mat-paginator> and <mat-sort> live inside *ngIf="!hidePaidTable",
  // so they are absent during the empty-state render. Setter-based ViewChilds
  // bind the dataSource the moment Angular renders them, so the footer
  // reflects actual row counts instead of a stale "0 of 0".
  //
  // Always write `s ?? null` to the shared dataSource so that when *ngIf flips
  // back to hidden, the dataSource releases its reference to the destroyed
  // MatSort/MatPaginator. PreviousPaymentsService is shared (singleton), so
  // holding a stale ref past the component lifecycle would leak the view and
  // leave the next visible setter in a "no wiring yet" state.
  private _sort?: MatSort;
  @ViewChild(MatSort) set sort(s: MatSort | undefined) {
    this._sort = s;
    this.previousPaymentsService.dataSourcePreviousPayments.sort = s ?? null;
  }
  get sort(): MatSort | undefined { return this._sort; }

  private _paginator?: MatPaginator;
  @ViewChild(MatPaginator) set paginator(p: MatPaginator | undefined) {
    this._paginator = p;
    this.previousPaymentsService.dataSourcePreviousPayments.paginator = p ?? null;
  }
  get paginator(): MatPaginator | undefined { return this._paginator; }

  @ViewChild('sidenav') public sidenav: MatSidenav;
  sortBy: any;

  @Input() searchString: string;

  // clientId used to store URL data
  // note: URL must use format specified in app-routing.module.ts
  clientId: string = '';

  // columns for material table
  columnsToDisplay = ['date', 'client', 'method', 'amount', 'status', 'invoiceNumber'];

  // Angular SelectionModel for selecting row on click.
  selection = new SelectionModel(true, [], true);

  // Hide table if there is no data.
  hidePaidTable = false;

  // Temp flag to help styling.
  paidStatus = false;

  // shownTimeSelectionOptions for dropdown table filter.
  shownTimeSelectionOptions = ['Show - All Time', 'Show - Past 3 Months', 'Show - Past 6 Months', 'Show - Past Year'];

  constructor(
    public invoicePdfService: InvoicePdfService,
    public dialog: MatDialog,
    public previousPaymentsService: PreviousPaymentsService,
    private paymentsApiService: PaymentsApiService,
    public configService: ConfigService,
    private redirectService: RedirectService,
  ) {}

  ngOnChanges() {
    this.applyFilter(this.searchString);
  }

  ngOnInit() {
    this.previousPaymentsService.previousPaymentsLoaded$.pipe(
      takeUntil(this._unsubscribe$),
    ).subscribe(() => {
      // Hide the table if there is no data.
      this.hidePaidTable = this.previousPaymentsService.dataSourcePreviousPayments.data.length === 0;
      // set filter predicate for previous payments table
      this.previousPaymentsService.dataSourcePreviousPayments.filterPredicate = (data: PaymentUi, filter: string) => {
        return this.previousPaymentsService.searchFilterFunction(data, filter);
      };
    })
  }


  /**
   * Pay action - redirect to pay component
   * TODO - call redirectAfterPay from PaymentConfirmationModal?
   */
  redirect(): void {
    this.redirectService.redirectAfterPay();
  }

  /**
   * used to implement search bar with input from invoices.component
   * @param filterValue
   */
  applyFilter(filterValue: string) {
    this.previousPaymentsService.dataSourcePreviousPayments.filter = filterValue ? filterValue.trim().toLowerCase() : '';
  }

  shownTimeSelectionChanged(value: string) {
    console.log(value);
    this.paymentsApiService.getPreviousPayments(this.previousPaymentsService.apiDateParameters(value)).pipe(
      takeUntil(this._unsubscribe$),
    ).subscribe(paymentUis => {
      // converts payment date time to the local time zone
      this.previousPaymentsService.dataSourcePreviousPayments.data = paymentUis.map(payment => {
        return PaymentDateService.formatLocalDateTimePayment(payment);
      });
    });
  }

  isDirectDebit(paymentMethodType: PaymentMethodType): boolean {
    return AbstractPaymentMethod.isDirectDebit(paymentMethodType);
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  /**
   * Downloads the invoice as a PDF via the fileID instead of the invoice number
   */
  downloadInvoiceAsPdf({ invoiceId, invoiceNumber }): void {
    this.invoicePdfService.exportInvoiceAsPDF(invoiceId, invoiceNumber);
  }
  /**
   * Previews the invoice as a PDF via the fileID instead of the invoice number
   */
  previewInvoiceAsPdf(invoiceId: number): void {
    this.invoicePdfService.previewInvoiceAsPDF(invoiceId);
  }
}
